/**
 * Financial Math Utilities
 */

/**
 * Rounds a number to a specific number of decimal places.
 */
export function roundToDecimal(value: number, places: number): number {
  if (value === undefined || value === null || isNaN(value) || !isFinite(value)) {
    return 0;
  }
  const factor = Math.pow(10, places);
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/**
 * PMT - Calculates the periodic payment for an annuity (loan) with constant payments and a constant interest rate.
 * Formula: PMT = [PV * r * (1 + r)^n] / [(1 + r)^n - 1]
 * @param rate - periodic interest rate (decimal, e.g. 0.05 / 12 for 5% monthly)
 * @param nper - total number of payments (months)
 * @param pv - present value (principal amount)
 * @param fv - future value (defaults to 0, e.g. amount left after term)
 * @param type - 0 = end of period (default), 1 = beginning of period
 */
export function pmt(
  rate: number,
  nper: number,
  pv: number,
  fv = 0,
  type = 0
): number {
  if (nper <= 0) {
    return 0;
  }
  // If interest rate is 0%, simple division
  if (rate === 0) {
    return -(pv + fv) / nper;
  }

  const pvif = Math.pow(1 + rate, nper);
  if (pvif === 1) {
    return 0;
  }
  let payment = (rate * (pv * pvif + fv)) / (pvif - 1);

  if (type === 1) {
    payment /= 1 + rate;
  }

  // Payments are usually returned as negative in financial calculators
  return -payment;
}

/**
 * CAGR - Compound Annual Growth Rate.
 * Formula: CAGR = (end / begin)^(1 / years) - 1
 */
export function cagr(beginValue: number, endValue: number, years: number): number {
  if (beginValue <= 0 || endValue <= 0 || years <= 0) return 0;
  return Math.pow(endValue / beginValue, 1 / years) - 1;
}
